import { useFormContext } from 'react-hook-form';
import { WorkflowMaterialFormValues } from '@forms/panel/workflows/material/types';
import { useTranslation } from 'react-i18next';
import { FormField } from '@components/ui/form';
import { FormItemWrapper } from '@components/ui/form/itemWrapper';
import { Input } from '@components/ui/form/input';
import { Textarea } from '@components/ui/form/textarea';
import { MaterialOptionsPropertyProps } from './types';
import { useEffect } from 'react';
import { cn } from '@utils/cn';

const TEXTAREA_CLASS_NAME =
  'h-8 min-h-[2rem] resize-y [scrollbar-width:none] [&::-webkit-scrollbar]:hidden';

export const MaterialOptionsProperty = ({
  record,
  index,
  isFinal,
  disabled,
}: MaterialOptionsPropertyProps) => {
  const { control, setValue, watch } = useFormContext<WorkflowMaterialFormValues>();
  const { t } = useTranslation('app');

  const finalQuantity = watch(`optionsAttributes.${index}.finalQuantity`);
  const useTextarea = record.valueType !== 'number' && !record.unit;

  useEffect(() => {
    if (!finalQuantity) return;
    if (record.includeEstimated) return;

    setValue(`optionsAttributes.${index}.estimatedQuantity`, finalQuantity);
  }, [finalQuantity, setValue, index, record]);

  return (
    <div className="flex flex-col gap-2">
      <div
        className={cn(
          'grid items-start gap-x-4 gap-y-2',
          record.includeEstimated ? 'grid-cols-2' : 'grid-cols-1',
        )}>
        {record.includeEstimated && (
          <FormField
            control={control}
            name={`optionsAttributes.${index}.estimatedQuantity`}
            render={({ field, fieldState: { error } }) => (
              <FormItemWrapper
                required={record.required}
                label={t('forms.estimated.labelWithName', { name: record.name })}>
                {useTextarea ? (
                  <Textarea
                    error={error?.message}
                    disabled={disabled}
                    className={TEXTAREA_CLASS_NAME}
                    {...field}
                  />
                ) : (
                  <Input
                    error={error?.message}
                    type={record.valueType === 'number' ? 'number' : 'text'}
                    rightAlignValue={record.valueType === 'number' && !!record.unit}
                    suffixElement={<strong>{record.unit}</strong>}
                    disabled={disabled}
                    {...field}
                  />
                )}
              </FormItemWrapper>
            )}
          />
        )}
        <FormField
          control={control}
          name={`optionsAttributes.${index}.finalQuantity`}
          render={({ field, fieldState: { error } }) => (
            <FormItemWrapper
              required={record.required && isFinal}
              label={
                record.includeEstimated
                  ? t('forms.final.labelWithName', { name: record.name })
                  : record.name
              }>
              {useTextarea ? (
                <Textarea
                  error={error?.message}
                  disabled={(record.includeEstimated && !isFinal) || disabled}
                  className={TEXTAREA_CLASS_NAME}
                  {...field}
                />
              ) : (
                <Input
                  error={error?.message}
                  type={record.valueType === 'number' ? 'number' : 'text'}
                  disabled={(record.includeEstimated && !isFinal) || disabled}
                  rightAlignValue={record.valueType === 'number' && !!record.unit}
                  suffixElement={<strong>{record.unit}</strong>}
                  {...field}
                />
              )}
            </FormItemWrapper>
          )}
        />
      </div>
    </div>
  );
};
